import { GhostMark } from "../GhostMark";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { useReviewsFeedback, useReviewsFunnel, useReviewsReferrals } from "../../queries";

function pct(a: number, b: number) {
  if (!b) return "0%";
  return `${Math.round((a / b) * 100)}%`;
}

function money(value: number) {
  return `$${Math.round(value || 0).toLocaleString()}`;
}

export function ReviewsContent() {
  const funnel = useReviewsFunnel();
  const feedback = useReviewsFeedback(20);
  const referrals = useReviewsReferrals(20);
  const steps = Object.fromEntries((funnel.data?.steps || []).map((s) => [s.name, s.count]));
  const sent = steps.review_sent || 0;
  const clicks = steps.smart_link_click || 0;
  const ratings = steps.rating_submitted || 0;
  const fiveStar = steps["5_star"] || 0;
  const publishedGoogleFiveStar = steps.published_google_5_star_since_launch || 0;
  const low = steps["1_to_3_star_rating"] ?? steps["1_to_3_star_feedback"] ?? 0;
  const windowLabel = funnel.data?.period_label === "custom" ? "selected period" : "since launch";

  return (
    <section className="space-y-5">
      <div>
        <Badge variant="slate">Reviews</Badge>
        <h1 className="mt-3 text-2xl font-semibold text-slate-950">Reviews</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          Review smart-link history, published Google reviews, and referral follow-up.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <FunnelCard label="Sent" value={sent} detail={`${pct(clicks, sent)} click-through ${windowLabel}`} />
        <FunnelCard label="Click" value={clicks} detail={`${pct(ratings, clicks)} submitted rating ${windowLabel}`} />
        <FunnelCard label="Rating" value={ratings} detail={`${pct(fiveStar, ratings)} 5-star ${windowLabel}`} />
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <FunnelCard label="5-star rate" value={pct(fiveStar, ratings)} detail={`${fiveStar} smart-link promoters captured`} />
        <FunnelCard label="Published Google 5-star" value={publishedGoogleFiveStar} detail="Since launch" />
        <FunnelCard label="Avg rating signal" value={low ? "Watch" : "Clean"} detail={`${low} low-rating rows`} />
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Low ratings (1-3 star) - needs follow-up</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {feedback.data?.items.length ? feedback.data.items.map((item) => (
              <div key={`${item.quote_id}-${item.received_at}`} className="rounded-md border border-slate-100 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium text-slate-950">{item.customer_name}<GhostMark ghosted={item.ghosted} /></div>
                  <Badge variant="red">{item.rating} star</Badge>
                </div>
                <p className="mt-1 text-xs text-slate-500">{item.customer_phone_masked} · #{item.quote_id} · {money(item.amount)}</p>
                <p className="mt-2 text-sm text-slate-700">{item.comment}</p>
              </div>
            )) : <Empty label={feedback.isLoading ? "Loading feedback..." : "No low-rating feedback right now."} />}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Referrals submitted</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {referrals.data?.items.length ? referrals.data.items.map((item) => (
              <div key={`${item.referrer_name}-${item.submitted_at}`} className="rounded-md border border-slate-100 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium text-slate-950">{item.referrer_name} to {item.referee_name}</div>
                  <Badge variant={item.status === "approved" ? "green" : "amber"}>{item.status}</Badge>
                </div>
                <p className="mt-1 text-xs text-slate-500">{item.referee_email_masked} · {item.referee_phone_masked}</p>
                <p className="mt-1 text-xs text-slate-500">Quote {item.attributed_quote_id || "pending"} · {money(item.attributed_amount)}</p>
              </div>
            )) : <Empty label={referrals.isLoading ? "Loading referrals..." : "No referral submissions right now."} />}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function FunnelCard({ label, value, detail }: { label: string; value: string | number; detail: string }) {
  return (
    <Card className="p-4">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-950">{value}</div>
      <div className="mt-1 text-xs text-slate-500">{detail}</div>
    </Card>
  );
}

function Empty({ label }: { label: string }) {
  return <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">{label}</div>;
}
